import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { ProductInterface, TopSuppliers, validateProduct } from "ts-interfaces";
import { Avatar, Button, DialogTitle, Grid, TextField } from "@mui/material";
import { Stack } from "@mui/system";
import AuthModal from "../studioxr/auth/AuthModal";
import { updateRecord } from "../libs/firebase";
import { produce } from "immer";
import Autocomplete from "@mui/material/Autocomplete";
import { materialCreator } from "./MaterialCreator";
import { getPreset, presetMaps } from "ui";
import * as Sentry from "@sentry/react";
import { ActionButton } from "../buttons/ButtonStyles";
import { Box } from "@mui/material";

interface FormValues {
  name: string;
  brand: string;
  preset: string;
}

interface PaintEditorProps {
  incomingProduct: ProductInterface;
  onSavedProduct: (product: ProductInterface) => void;
}

const textFieldProps = {
  variant: "outlined" as const,
  margin: "normal" as const,
  size: "small" as const,
  fullWidth: true,
  type: "text",
  sx: { paddingRight: "10px", width: "98%", marginTop: "8px" },
};

export const PaintEditor = ({
  incomingProduct,
  onSavedProduct,
}: PaintEditorProps) => {
  const modalState = materialCreator((state) => state.modalState);
  const setModalState = materialCreator((state) => state.setModalState);
  const saving = materialCreator((state) => state.saving);
  const setSaving = materialCreator((state) => state.setSaving);
  const [product, setProduct] = useState<ProductInterface | null>(null);
  const showModal = modalState === "paint_editor";

  const formik = useFormik<FormValues>({
    initialValues: {
      name: "Paint",
      brand: "",
      preset: Object.keys(presetMaps)[0],
    },
    onSubmit: () => {
      // Form is submitted programmatically via handleSubmit
    },
  });

  useEffect(() => {
    if (saving || !incomingProduct) {
      return;
    }

    formik.setValues({
      name: incomingProduct.name || "Paint",
      brand: incomingProduct.metadata?.brand || "",
      preset:
        incomingProduct.materialData?.preset || Object.keys(presetMaps)[0],
    });

    setProduct(incomingProduct);
  }, [incomingProduct, saving]);

  async function handleSubmit() {
    if (!product) return;

    if (formik.values.name.length < 3) {
      return alert("Please enter a name with at least 3 characters");
    }

    setSaving(true);

    try {
      const newProduct = produce(product, (draft) => {
        draft.name = formik.values.name;
        if (!draft.metadata) draft.metadata = {};
        draft.metadata.brand = formik.values.brand;
        if (draft.materialData) {
          draft.materialData.preset = formik.values.preset;
          draft.materialData.name = formik.values.name;
        }
        draft.updatedAt = Date.now();
      });

      validateProduct(newProduct);
      setProduct(newProduct);

      await updateRecord(newProduct.id, "userProducts", newProduct);

      // Dispatch event so other components know a material was updated
      document.dispatchEvent(
        new CustomEvent("userMaterialUpdate", {
          detail: { material: newProduct },
        })
      );

      onSavedProduct(newProduct);
    } catch (error) {
      console.log("Error updating paint:", error);
      Sentry.captureException(error);
    } finally {
      setSaving(false);
      setModalState(null);
    }
  }

  const handleBrandChange = (
    _event: React.SyntheticEvent,
    value: string | null
  ) => {
    formik.setFieldValue("brand", value || "");
  };

  const handleClose = () => {
    setModalState(null);
  };

  const handlePresetChange = (presetKey: string) => {
    formik.setFieldValue("preset", presetKey);
  };

  if (!showModal) return null;

  return (
    <AuthModal open={showModal} onClose={handleClose}>
      <Stack sx={{ paddingLeft: "10px", paddingRight: "10px" }}>
        <Grid
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "baseline",
          }}
        >
          <DialogTitle sx={{ paddingLeft: "0px", fontWeight: "bold" }}>
            Edit Paint
          </DialogTitle>
        </Grid>

        {product && (
          <Stack spacing={2} sx={{ mt: 2, mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <Box
                sx={{
                  background: product.materialData?.materialProps?.color
                    ? `#${product.materialData.materialProps.color.toString(
                        16
                      )}`
                    : "#ffffff",
                  borderRadius: "10px",
                  height: "120px",
                  width: "120px",
                  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                  mb: 1,
                }}
              />
            </Box>

            <TextField
              {...textFieldProps}
              id="name"
              label="Paint Name"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
            />

            <Autocomplete
              id="brand"
              freeSolo
              value={formik.values.brand}
              onChange={handleBrandChange}
              options={Object.values(TopSuppliers)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  {...textFieldProps}
                  label="Brand Name"
                  placeholder="Brand Name"
                  name="brand"
                  InputLabelProps={{
                    shrink: true,
                    style: { backgroundColor: "white", padding: "0 4px" },
                  }}
                />
              )}
            />

            <Autocomplete
              id="preset"
              value={formik.values.preset}
              onChange={(_event, newValue) => {
                if (newValue) handlePresetChange(newValue);
              }}
              options={Object.keys(presetMaps)}
              disableClearable
              getOptionLabel={(option) => {
                const preset = getPreset(option);
                return preset.name;
              }}
              renderOption={(props, option) => {
                const preset = getPreset(option);
                return (
                  <li {...props}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Avatar
                        sx={{ width: "25px", height: "20px", mr: 1 }}
                        src={preset.thumbnail}
                      />
                      {preset.name}
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  {...textFieldProps}
                  label="Preset Finish"
                  placeholder="Select a finish"
                  InputLabelProps={{
                    shrink: true,
                    style: { backgroundColor: "white", padding: "0 4px" },
                  }}
                />
              )}
            />
          </Stack>
        )}

        <Grid
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "end",
            alignItems: "center",
            padding: "10px",
            marginTop: "10px",
          }}
        >
          <ActionButton
            disabled={saving}
            style={{ width: "150px" }}
            variant="contained"
            onClick={handleSubmit}
          >
            {saving ? "Saving..." : "Update Paint"}
          </ActionButton>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              width: "100px",
              height: "30px",
              background: "white",
              borderColor: (theme) => theme.palette.primary.light,
              "&:hover": {
                background: (theme) => theme.palette.primary.light,
                borderColor: (theme) => theme.palette.primary.main,
                color: "black",
              },
            }}
          >
            Cancel
          </Button>
        </Grid>
      </Stack>
    </AuthModal>
  );
};
